import { EntityType } from './form-responses-types';

export const APPLICANT_TASK_MAPPING_PREFIX = 'applicant.task.';

export interface MappableTaskField {
  taskFieldId: string;
  name: string;
  typeName: string;
}

export interface MappableTask {
  taskId: string;
  title: string;
  cakeTitle: string;
  fields: MappableTaskField[];
}

export interface TaskFieldValuePayload {
  __typename: string;
  taskFieldId: string;
  [key: string]: unknown;
}

interface FieldOption {
  id: string;
  name: string;
}

const OPTION_KEYS: Record<string, { valueType: string; optionKey: string; optionsKey: string; multiple: boolean }> = {
  VOLUNTEER_DropdownTaskFieldType: {
    valueType: 'VOLUNTEER_DropdownTaskFieldValue',
    optionKey: 'dropdownTaskFieldOptionId',
    optionsKey: 'dropdownOptions',
    multiple: false,
  },
  VOLUNTEER_MultipleChoiceTaskFieldType: {
    valueType: 'VOLUNTEER_MultipleChoiceTaskFieldValue',
    optionKey: 'multipleChoiceTaskFieldOptionId',
    optionsKey: 'multipleChoiceOptions',
    multiple: false,
  },
  VOLUNTEER_CheckboxTaskFieldType: {
    valueType: 'VOLUNTEER_CheckboxTaskFieldValue',
    optionKey: 'checkboxTaskFieldOptionIds',
    optionsKey: 'checkboxOptions',
    multiple: true,
  },
};

const truthy = (raw: string): boolean => ['yes', 'true', '1', 'y'].includes(raw.trim().toLowerCase());

const numeric = (raw: string): number | undefined => {
  const parsed = Number(raw.trim());
  return Number.isNaN(parsed) ? undefined : parsed;
};

const matchOptions = (raw: string, options: FieldOption[], multiple: boolean): string[] => {
  const wanted = (multiple ? raw.split(/[\n,]/) : [raw]).map((part) => part.trim().toLowerCase()).filter(Boolean);
  return options.filter((option) => wanted.includes(option.name.trim().toLowerCase())).map((option) => option.id);
};

export const buildTaskFieldValue = (
  field: { taskFieldId: string; typeName: string; options?: FieldOption[] },
  raw: string
): TaskFieldValuePayload | undefined => {
  const value = raw.trim();
  if (!value) return undefined;

  const base = { taskFieldId: field.taskFieldId };
  const optionSpec = OPTION_KEYS[field.typeName];

  if (optionSpec) {
    const matched = matchOptions(value, field.options ?? [], optionSpec.multiple);
    if (matched.length === 0) return undefined;
    return {
      ...base,
      __typename: optionSpec.valueType,
      [optionSpec.optionKey]: optionSpec.multiple ? matched : matched[0],
    };
  }

  switch (field.typeName) {
    case 'VOLUNTEER_ShortTextTaskFieldType':
      return { ...base, __typename: 'VOLUNTEER_ShortTextTaskFieldValue', shortTextValue: value };
    case 'VOLUNTEER_LongTextTaskFieldType':
      return { ...base, __typename: 'VOLUNTEER_LongTextTaskFieldValue', longTextValue: value };
    case 'VOLUNTEER_EmailTaskFieldType':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? { ...base, __typename: 'VOLUNTEER_EmailTaskFieldValue', emailValue: value }
        : undefined;
    case 'VOLUNTEER_PhoneTaskFieldType':
      return { ...base, __typename: 'VOLUNTEER_PhoneTaskFieldValue', phoneValue: value };
    case 'VOLUNTEER_UrlTaskFieldType':
      return /^https?:\/\/\S+$/i.test(value)
        ? { ...base, __typename: 'VOLUNTEER_UrlTaskFieldValue', urlValue: value }
        : undefined;
    case 'VOLUNTEER_DateTaskFieldType':
      return Number.isNaN(Date.parse(value))
        ? undefined
        : { ...base, __typename: 'VOLUNTEER_DateTaskFieldValue', dateValue: value };
    case 'VOLUNTEER_TimeTaskFieldType':
      return /^\d{1,2}:\d{2}(:\d{2})?$/.test(value)
        ? { ...base, __typename: 'VOLUNTEER_TimeTaskFieldValue', timeValue: value }
        : undefined;
    case 'VOLUNTEER_YesNoTaskFieldType':
      return { ...base, __typename: 'VOLUNTEER_YesNoTaskFieldValue', yesNoValue: truthy(value) };
    default:
      break;
  }

  const parsed = numeric(value);
  if (parsed === undefined) return undefined;

  switch (field.typeName) {
    case 'VOLUNTEER_NumberTaskFieldType':
      return { ...base, __typename: 'VOLUNTEER_NumberTaskFieldValue', numericValue: parsed };
    case 'VOLUNTEER_RatingTaskFieldType':
      return { ...base, __typename: 'VOLUNTEER_RatingTaskFieldValue', ratingValue: parsed };
    case 'VOLUNTEER_LinearScaleTaskFieldType':
      return { ...base, __typename: 'VOLUNTEER_LinearScaleTaskFieldValue', linearScaleValue: parsed };
    case 'VOLUNTEER_NpsTaskFieldType':
      return { ...base, __typename: 'VOLUNTEER_NpsTaskFieldValue', npsValue: parsed };
    default:
      return undefined;
  }
};


type TypeFamily = 'text' | 'email' | 'phone' | 'date' | 'time' | 'url' | 'number' | 'boolean' | 'options';

const FIELD_TYPE_FAMILY: Record<string, TypeFamily> = {
  VOLUNTEER_ShortTextTaskFieldType: 'text',
  VOLUNTEER_LongTextTaskFieldType: 'text',
  VOLUNTEER_EmailTaskFieldType: 'email',
  VOLUNTEER_PhoneTaskFieldType: 'phone',
  VOLUNTEER_DateTaskFieldType: 'date',
  VOLUNTEER_TimeTaskFieldType: 'time',
  VOLUNTEER_UrlTaskFieldType: 'url',
  VOLUNTEER_NumberTaskFieldType: 'number',
  VOLUNTEER_RatingTaskFieldType: 'number',
  VOLUNTEER_LinearScaleTaskFieldType: 'number',
  VOLUNTEER_NpsTaskFieldType: 'number',
  VOLUNTEER_YesNoTaskFieldType: 'boolean',
  VOLUNTEER_DropdownTaskFieldType: 'options',
  VOLUNTEER_MultipleChoiceTaskFieldType: 'options',
  VOLUNTEER_CheckboxTaskFieldType: 'options',
};

/**
 * Which source families can fill a target family. A target only accepts a
 * source whose values it can actually represent, so a free-text answer is
 * never offered for a date, and an email is never taken from a phone number.
 */
const ACCEPTS: Record<TypeFamily, TypeFamily[]> = {
  text: ['text', 'email', 'phone', 'date', 'time', 'url', 'number', 'boolean', 'options'],
  email: ['email'],
  phone: ['phone'],
  date: ['date'],
  time: ['time'],
  url: ['url'],
  number: ['number'],
  boolean: ['boolean'],
  options: ['options', 'text'],
};

export const familyOf = (typeName?: string): TypeFamily | undefined =>
  typeName ? FIELD_TYPE_FAMILY[typeName] : undefined;

export const canFillTarget = (targetTypeName: string, sourceTypeName?: string): boolean => {
  const target = familyOf(targetTypeName);
  if (!target) return false;

  const source = familyOf(sourceTypeName);
  if (!source) return target === 'text';

  return ACCEPTS[target].includes(source);
};

/** Target type for each of the applicant identity mappings. */
export const APPLICANT_IDENTITY_TARGET_TYPES: Record<string, string> = {
  'applicant.email': 'VOLUNTEER_EmailTaskFieldType',
  'applicant.firstName': 'VOLUNTEER_ShortTextTaskFieldType',
  'applicant.lastName': 'VOLUNTEER_ShortTextTaskFieldType',
  'applicant.contactNumber': 'VOLUNTEER_PhoneTaskFieldType',
  'applicant.dob': 'VOLUNTEER_DateTaskFieldType',
};

export const UNMAPPABLE_TASK_FIELD_TYPES = [
  'VOLUNTEER_AttachmentTaskFieldType',
  'VOLUNTEER_SignatureTaskFieldType',
  'VOLUNTEER_AddressTaskFieldType',
  'VOLUNTEER_MatrixTaskFieldType',
  'VOLUNTEER_QuizTaskFieldType',
  'VOLUNTEER_CrossTaskDynamicTaskFieldType',
];

export const isMappableTaskFieldType = (typeName: string): boolean =>
  !UNMAPPABLE_TASK_FIELD_TYPES.includes(typeName);

export const applicantTaskMappingKey = (taskId: string, taskFieldId: string): string =>
  `${APPLICANT_TASK_MAPPING_PREFIX}${taskId}.${taskFieldId}`;

export const isApplicantEntityType = (entityType: EntityType | null): boolean => entityType === 'applicant';
